// Browser client for the Lexody games API (Node). Rails hands out a short-lived
// API token at /games/auth for the signed-in Devise user.

const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

let authUrl = '/games/auth';
let credentials = null;
let pendingCredentials = null;

export class GameApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

export function configureGamesApi(url) {
  if (url) authUrl = url;
}

function credentialsFresh() {
  return credentials
    && new Date(credentials.expires_at).getTime() - Date.now() > TOKEN_REFRESH_MARGIN_MS;
}

export function loadCredentials({ force = false } = {}) {
  if (!force && credentialsFresh()) return Promise.resolve(credentials);
  if (!pendingCredentials) {
    pendingCredentials = fetch(authUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new GameApiError(
            body.error || 'Could not connect you to games. Refresh the page and try again.',
            res.status
          );
        }
        credentials = body;
        return body;
      })
      .finally(() => {
        pendingCredentials = null;
      });
  }
  return pendingCredentials;
}

async function request(method, path, body, retried = false) {
  const creds = await loadCredentials();
  let res;
  try {
    res = await fetch(`${creds.api_url}/api/games${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${creds.api_token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    throw new GameApiError('Connection problem. Check your internet and try again.', 0);
  }

  if (res.status === 401 && !retried) {
    await loadCredentials({ force: true });
    return request(method, path, body, true);
  }

  const json = await res.json().catch(() => null);
  if (!res.ok) {
    throw new GameApiError((json && json.error) || `Request failed (${res.status})`, res.status);
  }
  return json.response;
}

export function gameErrorMessage(error) {
  return (error && error.message) || 'Something went wrong. Please try again.';
}

const gameSessionsApi = {
  packs: () => request('GET', '/packs'),
  create: (body) => request('POST', '', body),
  join: (code) => request('POST', '/join', { code }),
  get: (id) => request('GET', `/${id}`),
  setPictureSet: (id, pictureSet) => request('POST', `/${id}/picture-set`, { picture_set: pictureSet }),
  start: (id, { practice = true } = {}) => request('POST', `/${id}/start`, { practice }),
  endPractice: (id) => request('POST', `/${id}/onboarding/complete`),
  guess: (id, position) => request('POST', `/${id}/guess`, { position }),
  timeout: (id) => request('POST', `/${id}/timeout`),
  complete: (id) => request('POST', `/${id}/complete`),
  realtimeLink: () => request('POST', '/realtime-link'),
};

export default gameSessionsApi;
